import { useCallback, useEffect, useRef, useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import { ExpressPaymentType } from '@stripe/stripe-js'

import { WalletCheckoutSession } from 'src/Components/WalletExpressCheckout/WalletExpressCheckoutForm'
import {
  CouponApplyResult,
  ScheduleSummaryProps,
} from 'src/Components/ScheduleSummary'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { useCurrency } from 'src/Hooks/useCurrency'
import { getFormattedCheckoutTimer } from 'src/Redux/checkoutCountdown-process'
import {
  getIsEnoughBalanceSelector,
  spotifyTotalPriceSelector,
} from 'src/Redux/spotifyCampaignMatchingProcess'
import { selectManualMatchingBudget } from 'src/Redux/selectedCuratorsSlice'
import {
  getIsManualMatchingCampaign,
  getScheduleMatchingType,
} from 'src/Hooks/Experiments/useManualMatchingExperiment'
import { useBonusTooltipTimer } from 'src/Hooks/useBonusTooltip'
import { getCouponToKeep, getPayableCoupon } from 'src/Hooks/useThresholdCoupon'
import { useGetBillingAgreementQuery } from 'src/Redux/billingAgreementApi'
import { getArtistBalance } from 'src/Redux/auth-process/userSlice/selectors'
import { useOrderCheckoutConfirmEvents } from 'src/Hooks/useOrderCheckoutConfirmEvents'
import { PaymentMethodEnum } from 'src/Constants/enums'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { getRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process/selectors'
import { setPaymentAlertError } from 'src/Redux/payment-process'
import { PaymentErrors } from 'src/Components/PaymentErrorsAlert'
import { getOrderCheckoutPaymentError } from 'src/Utils/orderCheckoutPaymentError'
import { refetchUsersMe } from 'src/Redux/userInfoApi'
import { ScheduleSteps } from 'src/Types/ui'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { getDefaultStartDate } from 'src/Constants/functions'
import { createPaymentUrls } from 'src/Hooks/useStripePayment'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import {
  Order,
  OrderCheckoutInfoResponse,
  OrderItem,
  OrderItemType,
  OrderPaymentMethod,
  useAddOrderItemMutation,
  useCreateOrderCheckoutMutation,
  useCreateOrderMutation,
  useGetCurrentOrderQuery,
  useLazyGetOrderCheckoutInfoQuery,
  useRemoveOrderItemsMutation,
  useUpdateOrderItemMutation,
} from 'src/Redux/artistOrderApi'
import { isOrderLocked } from 'src/Redux/artistOrderApi/isOrderLocked'
import { useFindConflictingOrder } from 'src/Redux/artistOrderApi/useFindConflictingOrder'
import {
  getIsCoveredByBalance,
  getIsOrderFullyCovered,
} from 'src/Utils/displayedPaymentMethods'

import { useSpotifyPrioritizedLaunch } from './useSpotifyPrioritizedLaunch'
import { useSpotifyThresholdCoupon } from './useSpotifyThresholdCoupon'

const ORDER_PAYMENT_METHOD_MAP: Record<PaymentMethodEnum, OrderPaymentMethod> =
  {
    [PaymentMethodEnum.Credits]: OrderPaymentMethod.BalanceOnly,
    [PaymentMethodEnum.STRIPE]: OrderPaymentMethod.StripeOneTime,
    [PaymentMethodEnum.PAYPAL]: OrderPaymentMethod.PaypalOneTime,
    [PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT]:
      OrderPaymentMethod.PaypalBillingAgreement,
    [PaymentMethodEnum.APPLE_PAY]: OrderPaymentMethod.StripeWallet,
    [PaymentMethodEnum.GOOGLE_PAY]: OrderPaymentMethod.StripeWallet,
  }

const ORDER_PAYMENT_METHOD_TO_ENUM: Partial<
  Record<OrderPaymentMethod, PaymentMethodEnum>
> = {
  [OrderPaymentMethod.BalanceOnly]: PaymentMethodEnum.Credits,
  [OrderPaymentMethod.StripeOneTime]: PaymentMethodEnum.STRIPE,
  [OrderPaymentMethod.PaypalOneTime]: PaymentMethodEnum.PAYPAL,
  [OrderPaymentMethod.PaypalBillingAgreement]:
    PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT,
}

const findCouponError = (
  data: OrderCheckoutInfoResponse,
  couponCode: string,
): CouponApplyResult['coupon']['error'] => {
  const topLevelError = data.couponErrors.find(
    (item) => item.couponCode === couponCode,
  )

  if (topLevelError?.error) {
    return { message: topLevelError.error, param: topLevelError.param ?? null }
  }

  const itemDiscount = data.items
    .flatMap((item) => item.discounts)
    .find((discount) => discount.couponCode === couponCode)

  if (itemDiscount?.error) {
    return { message: itemDiscount.error, param: itemDiscount.param ?? null }
  }

  return null
}

const hasCouponImmediateDiscount = (
  data: OrderCheckoutInfoResponse,
  couponCode: string,
): boolean =>
  data.items
    .flatMap((item) => item.discounts)
    .some((d) => d.couponCode === couponCode && d.discountAmount > 0)

const mapToCouponApplyResult = (
  data: OrderCheckoutInfoResponse,
  couponCode: string,
): CouponApplyResult => {
  const error = findCouponError(data, couponCode)
  return {
    coupon: {
      error,
      isBonusCoupon: !error && !hasCouponImmediateDiscount(data, couponCode),
    },
  }
}

const resolveOrderPaymentMethod = (
  paymentMethod: PaymentMethodEnum,
  isRememberPayPalForFuture: boolean,
  paypalEmail?: string,
): OrderPaymentMethod => {
  if (
    paymentMethod === PaymentMethodEnum.PAYPAL &&
    isRememberPayPalForFuture &&
    !paypalEmail
  ) {
    return OrderPaymentMethod.PaypalBillingAgreement
  }
  return (
    ORDER_PAYMENT_METHOD_MAP[paymentMethod] || OrderPaymentMethod.BalanceOnly
  )
}

const getShowPromocode = (
  isOrderFullyCovered: boolean,
  hasDiscount: boolean,
): boolean => !isOrderFullyCovered && !hasDiscount

const toCouponList = (coupon: string): string[] => (coupon ? [coupon] : [])

const getCheckoutCoupon = (
  balance: number,
  checkoutInfo: OrderCheckoutInfoResponse | null | undefined,
  appliedCoupon: string,
): string =>
  getIsCoveredByBalance(balance, checkoutInfo)
    ? ''
    : getPayableCoupon(checkoutInfo, appliedCoupon)

const isSameCampaignOrderItem = (
  item: OrderItem,
  campaignId: number,
): boolean =>
  item.sourceId === campaignId &&
  (item.type === OrderItemType.SpotifyCampaign ||
    item.type === OrderItemType.SpotifyPrioritizedLaunch)

const WALLET_PAYMENT_METHOD_MAP: Partial<
  Record<ExpressPaymentType, PaymentMethodEnum>
> = {
  google_pay: PaymentMethodEnum.GOOGLE_PAY,
  apple_pay: PaymentMethodEnum.APPLE_PAY,
}

type UseSpotifyScheduleOrderSummary = ScheduleSummaryProps & {
  availablePaymentMethods?: PaymentMethodEnum[]
  isOrderFullyCovered: boolean
  isSyncing: boolean
  isWalletPaymentEligible: boolean
  walletAmount: number
  onConfirmWalletPayment: (
    expressPaymentType: ExpressPaymentType,
  ) => Promise<WalletCheckoutSession | null>
  onWalletPaymentSuccess: () => void
  onWalletPaymentError: () => void
}

export const useSpotifyScheduleOrderSummary =
  (): UseSpotifyScheduleOrderSummary => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { number } = useParams()
    const { search } = useCustomSearchParams()
    const campaignId = Number(number)
    const isPaymentSucceeded = search.get('paymentStatus') === 'success'

    const { campaignDetails, matchingType, isPreRelease } =
      useSpotifyCampaignStatus()
    const paymentMethod = useAppSelector(getPaymentMethod)
    const isRememberPayPalForFuture = useAppSelector(
      getRememberPayPalForFutureFlag,
    )
    const { data: billingData } = useGetBillingAgreementQuery()
    const isEnoughBalance = useAppSelector(getIsEnoughBalanceSelector)
    const balance = useAppSelector(getArtistBalance)
    const countdown = useAppSelector(getFormattedCheckoutTimer)
    const spotifyTotalPrice = useAppSelector(spotifyTotalPriceSelector)
    const manualMatchingBudget = useAppSelector(selectManualMatchingBudget)

    const isManualMatching = getIsManualMatchingCampaign(matchingType)
    const budget = isManualMatching ? manualMatchingBudget : spotifyTotalPrice

    const [appliedCoupon, setAppliedCoupon] = useState('')
    const initializedOrderIdRef = useRef<number | null>(null)
    const isCreatingOrderRef = useRef(false)

    const { startNextTimeBonusCountdown, isNextTimeBonus } =
      useBonusTooltipTimer({
        platform: CampaignPlatformTypeEnum.SPOTIFY,
        budget,
      })

    const { calculateCurrencyValue } = useCurrency(
      CampaignPlatformTypeEnum.SPOTIFY,
    )

    const triggerConfirmPaymentEvents = useOrderCheckoutConfirmEvents(
      CampaignPlatformTypeEnum.SPOTIFY,
    )

    const { data: currentOrder, isFetching: isCurrentOrderFetching } =
      useGetCurrentOrderQuery()
    const { order: conflictingOrder, isFetching: isConflictingOrderFetching } =
      useFindConflictingOrder(
        OrderItemType.SpotifyCampaign,
        campaignId,
        currentOrder?.id,
      )
    const [createOrder] = useCreateOrderMutation()
    const [addOrderItem] = useAddOrderItemMutation()
    const [updateOrderItem] = useUpdateOrderItemMutation()
    const [removeOrderItems] = useRemoveOrderItemsMutation()
    const [createOrderCheckout, { isLoading }] =
      useCreateOrderCheckoutMutation()
    const [triggerCheckoutInfo, { data: checkoutInfoData }] =
      useLazyGetOrderCheckoutInfoQuery()

    const payableCoupon = getCheckoutCoupon(
      balance,
      checkoutInfoData,
      appliedCoupon,
    )

    const refetchCheckoutInfo = useCallback(async (): Promise<void> => {
      const data = await triggerCheckoutInfo({
        coupons: toCouponList(appliedCoupon),
      }).unwrap()

      if (appliedCoupon && getIsCoveredByBalance(balance, data)) {
        setAppliedCoupon('')
        await triggerCheckoutInfo({ coupons: [] }).unwrap()
      }
    }, [appliedCoupon, balance, triggerCheckoutInfo])

    const isOrderDataPending =
      isCurrentOrderFetching ||
      isConflictingOrderFetching ||
      currentOrder === void 0

    useEffect(() => {
      if (isPaymentSucceeded || !budget || isOrderDataPending) {
        return
      }

      const syncActiveOrder = async (order: Order): Promise<void> => {
        if (initializedOrderIdRef.current === order.id) {
          return
        }
        initializedOrderIdRef.current = order.id

        const foreignItemIds = order.items
          .filter((item) => !isSameCampaignOrderItem(item, campaignId))
          .map((item) => item.id)

        if (foreignItemIds.length) {
          await removeOrderItems({
            orderId: order.id,
            itemIds: foreignItemIds,
          }).unwrap()
        }

        const existingItem = order.items.find(
          (item) =>
            item.type === OrderItemType.SpotifyCampaign &&
            item.sourceId === campaignId,
        )

        if (existingItem) {
          if (existingItem.fullPrice !== budget) {
            await updateOrderItem({
              orderId: order.id,
              itemId: existingItem.id,
              fullPrice: budget,
            }).unwrap()
          }
        } else {
          await addOrderItem({
            orderId: order.id,
            type: OrderItemType.SpotifyCampaign,
            sourceId: campaignId,
            fullPrice: budget,
          }).unwrap()
        }

        await triggerCheckoutInfo({ coupons: [] }).unwrap()
      }

      const initializeOrder = async (): Promise<void> => {
        if (conflictingOrder) {
          navigate(`${ArtistRoutesEnum.SpotifyDetails}/${campaignId}`)
          return
        }

        if (!currentOrder) {
          if (isCreatingOrderRef.current) {
            return
          }
          isCreatingOrderRef.current = true
          try {
            await createOrder().unwrap()
          } finally {
            isCreatingOrderRef.current = false
          }
          return
        }

        if (isOrderLocked(currentOrder.status)) {
          initializedOrderIdRef.current = null
          return
        }

        await syncActiveOrder(currentOrder)
      }

      initializeOrder().catch(() => {
        initializedOrderIdRef.current = null
        dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
      })
    }, [
      addOrderItem,
      budget,
      campaignId,
      conflictingOrder,
      createOrder,
      currentOrder,
      dispatch,
      isOrderDataPending,
      isPaymentSucceeded,
      navigate,
      removeOrderItems,
      triggerCheckoutInfo,
      updateOrderItem,
    ])

    useEffect(() => {
      startNextTimeBonusCountdown()
    }, [startNextTimeBonusCountdown])

    const onApplyPromocode = async (
      couponCode: string,
    ): Promise<CouponApplyResult | undefined> => {
      const data = await triggerCheckoutInfo({
        coupons: [couponCode],
      }).unwrap()
      if (!data) {
        return void 0
      }

      setAppliedCoupon(
        getCouponToKeep(findCouponError(data, couponCode), couponCode),
      )

      return mapToCouponApplyResult(data, couponCode)
    }

    const handleRemoveCoupon = (): void => {
      setAppliedCoupon('')
      void triggerCheckoutInfo({ coupons: [] })
    }

    const startDate =
      campaignDetails?.start_date?.toString() || getDefaultStartDate()

    const { prioritizedLaunch } = useSpotifyPrioritizedLaunch({
      campaignDetails,
      campaignId,
      budget,
      startDate,
      isManualMatching,
      calculateCurrencyValue,
      orderId: currentOrder?.id,
      orderItems: currentOrder?.items,
      orderStatus: currentOrder?.status,
      onAfterMutation: refetchCheckoutInfo,
    })

    const breakdown = checkoutInfoData?.breakdown
    const isOrderFullyCovered = getIsOrderFullyCovered(
      breakdown,
      isEnoughBalance,
    )

    const {
      thresholdCoupon,
      isSyncing,
      onApplyPromocode: onApplyPromocodeWithOffer,
    } = useSpotifyThresholdCoupon({
      formatAmount: calculateCurrencyValue,
      onRemoveCoupon: handleRemoveCoupon,
      campaignId,
      budget,
      appliedCoupon,
      isManualMatching,
      isOrderFullyCovered,
      isPaymentSucceeded,
      currentOrder,
      checkoutInfoData,
      onApplyPromocode,
      updateOrderItem,
      triggerCheckoutInfo,
    })

    const navigateToPaymentSuccess = useCallback((): void => {
      navigate(
        `/campaigns/schedule/${campaignId}?step=${ScheduleSteps.CHECKOUT}&paymentStatus=success&startDate=${encodeURIComponent(startDate)}`,
      )
      void dispatch(refetchUsersMe())
    }, [campaignId, dispatch, navigate, startDate])

    const handleClickContinue = useCallback((): void => {
      if (!checkoutInfoData || isSyncing) {
        return
      }

      const orderPaymentMethod = resolveOrderPaymentMethod(
        paymentMethod,
        isRememberPayPalForFuture,
        billingData?.paypalEmail,
      )

      if (!checkoutInfoData.paymentMethods.includes(orderPaymentMethod)) {
        dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
        return
      }

      const { successUrl, cancelUrl } = createPaymentUrls({
        campaignId,
        startDate,
        type: CampaignPlatformTypeEnum.SPOTIFY,
        budget: checkoutInfoData.breakdown.totalFullPrice,
        coupon: payableCoupon,
      })

      const itemBreakdowns = checkoutInfoData.items.map((item) => ({
        id: item.id,
        fullPrice: item.fullPrice,
        appliedBalance: item.appliedBalance,
        payable: item.payable,
        itemDiscount: item.itemDiscount,
        vatAmount: item.vatAmount,
      }))

      triggerConfirmPaymentEvents()

      createOrderCheckout({
        paymentMethod: orderPaymentMethod,
        coupons: toCouponList(payableCoupon),
        itemBreakdowns,
        successUrl,
        cancelUrl,
      })
        .unwrap()
        .then((result) => {
          if (result.checkoutSessionUrl) {
            window.location.href = result.checkoutSessionUrl
            return
          }

          navigateToPaymentSuccess()
        })
        .catch((error: unknown) => {
          dispatch(setPaymentAlertError(getOrderCheckoutPaymentError(error)))
        })
    }, [
      payableCoupon,
      billingData?.paypalEmail,
      campaignId,
      checkoutInfoData,
      createOrderCheckout,
      dispatch,
      isSyncing,
      isRememberPayPalForFuture,
      navigateToPaymentSuccess,
      paymentMethod,
      startDate,
      triggerConfirmPaymentEvents,
    ])

    const handleWalletPaymentError = useCallback((): void => {
      dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
    }, [dispatch])

    const handleConfirmWalletPayment = useCallback(
      async (
        expressPaymentType: ExpressPaymentType,
      ): Promise<WalletCheckoutSession | null> => {
        if (isSyncing) {
          return null
        }

        if (
          !checkoutInfoData?.paymentMethods.includes(
            OrderPaymentMethod.StripeWallet,
          )
        ) {
          handleWalletPaymentError()
          return null
        }

        const { successUrl, cancelUrl } = createPaymentUrls({
          campaignId,
          startDate,
          type: CampaignPlatformTypeEnum.SPOTIFY,
          budget: checkoutInfoData.breakdown.totalFullPrice,
          coupon: payableCoupon,
        })

        const itemBreakdowns = checkoutInfoData.items.map((item) => ({
          id: item.id,
          fullPrice: item.fullPrice,
          appliedBalance: item.appliedBalance,
          payable: item.payable,
          itemDiscount: item.itemDiscount,
          vatAmount: item.vatAmount,
        }))

        triggerConfirmPaymentEvents(
          WALLET_PAYMENT_METHOD_MAP[expressPaymentType],
        )

        try {
          const result = await createOrderCheckout({
            paymentMethod: OrderPaymentMethod.StripeWallet,
            coupons: toCouponList(payableCoupon),
            itemBreakdowns,
            successUrl,
            cancelUrl,
          }).unwrap()

          if (!result.checkoutSessionUrl) {
            handleWalletPaymentError()
            return null
          }

          return {
            clientSecret: result.checkoutSessionUrl,
            returnUrl: successUrl,
          }
        } catch {
          handleWalletPaymentError()
          return null
        }
      },
      [
        payableCoupon,
        campaignId,
        checkoutInfoData,
        createOrderCheckout,
        handleWalletPaymentError,
        isSyncing,
        startDate,
        triggerConfirmPaymentEvents,
      ],
    )

    const availablePaymentMethods = checkoutInfoData?.paymentMethods
      .map((method) => ORDER_PAYMENT_METHOD_TO_ENUM[method])
      .filter((method): method is PaymentMethodEnum => Boolean(method))

    return {
      availablePaymentMethods,
      isOrderFullyCovered,
      countdown,
      thresholdCoupon,
      isSyncing,
      isPreRelease,
      prioritizedLaunch,
      onApplyPromocode: onApplyPromocodeWithOffer,
      matchingType: getScheduleMatchingType(matchingType),
      onConfirm: handleClickContinue,
      isWalletPaymentEligible: Boolean(
        checkoutInfoData?.paymentMethods.includes(
          OrderPaymentMethod.StripeWallet,
        ),
      ),
      walletAmount: breakdown?.totalPayable || 0,
      onConfirmWalletPayment: handleConfirmWalletPayment,
      onWalletPaymentSuccess: navigateToPaymentSuccess,
      onWalletPaymentError: handleWalletPaymentError,
      paymentLoading: isLoading,
      price: calculateCurrencyValue(breakdown?.totalFullPrice),
      discount: breakdown?.totalDiscount
        ? calculateCurrencyValue(breakdown.totalDiscount, true)
        : '',
      vat: breakdown?.totalVatAmount
        ? `+ ${calculateCurrencyValue(breakdown.totalVatAmount)}`
        : '',
      balance: `- ${calculateCurrencyValue(breakdown?.totalAppliedBalance)}`,
      total: calculateCurrencyValue(breakdown?.totalPayable),
      showPromocode: getShowPromocode(
        isOrderFullyCovered,
        Boolean(breakdown?.totalDiscount),
      ),
      showTooltip: isNextTimeBonus,
      artistName: campaignDetails?.track.author || '',
      trackName: campaignDetails?.track.title || '',
      platform: CampaignPlatformTypeEnum.SPOTIFY,
    }
  }
